import { applyAction, createGame, findLegal } from './engine';
import { parseSq, sqName } from './geometry';
import { ALL_PIECES, defaultRules } from './presets';
import { validateRuleset } from './validate';
import type { AbilityKind, Action, Color, GameRules, GameState, PieceTypeDef, Placement, Ruleset } from './types';

export interface Puzzle {
  id: string;
  title: string;
  hint: string;
  ruleset: Ruleset;
  toMove: Color;
  /**
   * A winning line: comma-separated actions, each "from-to", "from-to=promotion" or
   * "from:ability[:target]". Several actions are only needed with several moves per turn.
   */
  solution: string;
}

const L: Record<string, string> = { K: 'king', Q: 'queen', R: 'rook', B: 'bishop', N: 'knight', P: 'pawn', D: 'dragon', O: 'bomb', M: 'miner', F: 'frog', G: 'gardener', E: 'elephant', X: 'cockroach', C: 'cook' };

function rs(id: string, pieces: string, opts: { rules?: Partial<GameRules>; voids?: string[]; patch?: (d: Record<string, PieceTypeDef>) => void } = {}): Ruleset {
  const setup: Placement[] = pieces.split(/\s+/).map((tok) => ({ color: tok[0] as Color, type: L[tok[1]], sq: tok.slice(2) }));
  const defs: Record<string, PieceTypeDef> = {};
  for (const p of setup) defs[p.type] = JSON.parse(JSON.stringify(ALL_PIECES[p.type]));
  for (const t of ['queen', 'rook', 'bishop', 'knight']) defs[t] ??= JSON.parse(JSON.stringify(ALL_PIECES[t]));
  opts.patch?.(defs);
  return {
    schema: 1, id: 'puzzle-' + id, name: 'Puzzle', board: { width: 8, height: 8, voids: opts.voids ?? [], walls: [] },
    pieces: defs, setup, rules: { ...defaultRules(), castling: false, ...opts.rules },
  };
}

const KING_HUNT: Partial<GameRules> = {
  check: false,
  winConditions: { checkmate: false, captureAllRoyals: true, captureAnyRoyal: true, reachSquare: false },
};

export const PUZZLES: Puzzle[] = [
  {
    id: 'back-rank', title: 'Back rank', hint: 'The black king is trapped behind its own pawns.',
    ruleset: rs('back-rank', 'wKg1 wRa1 bKg8 bPf7 bPg7 bPh7'), toMove: 'w', solution: 'a1-a8',
  },
  {
    id: 'scholar', title: 'Weak f7', hint: 'Queen and bishop both aim at the same square.',
    ruleset: rs('scholar', 'wKe1 wQh5 wBc4 wPe4 bKe8 bQd8 bRa8 bRh8 bBc8 bBf8 bNc6 bNf6 bPa7 bPb7 bPc7 bPd7 bPe5 bPf7 bPg7 bPh7'),
    toMove: 'w', solution: 'h5-f7',
  },
  {
    id: 'dragon-breath', title: 'Dragon fire', hint: 'Dragons do not have to move to capture.',
    ruleset: rs('dragon-breath', 'wKa1 wDe4 bKe7 bRe8 bPd7 bPf7 bPd6 bPf6 bNa8', { rules: KING_HUNT }),
    toMove: 'w', solution: 'e4:fireBreath:e7',
  },
  {
    id: 'bomb-defuse', title: 'Too close for comfort', hint: 'Bombs destroy everything around them.',
    ruleset: rs('bomb', 'wKa1 wOe7 bKd8 bQh4 bRa8', { rules: KING_HUNT }), toMove: 'w', solution: 'e7:detonate',
  },
  {
    id: 'chain', title: 'Chain reaction', hint: 'A blast sets off every bomb it touches, even the enemy’s.',
    ruleset: rs('chain', 'wKh1 wOa5 bOb6 bKc7 bQh8', { rules: KING_HUNT }), toMove: 'w', solution: 'a5:detonate',
  },
  {
    id: 'sweep', title: 'Into the abyss', hint: 'Pieces pushed into a hole are gone.',
    ruleset: rs('sweep', 'wKa1 wDe6 bKe7 bPd7 bPf7 bRh8', {
      rules: KING_HUNT, voids: ['e8'],
      patch: (d) => { d.dragon.abilities = d.dragon.abilities.filter((a) => a.kind !== 'fireBreath'); d.dragon.moves[0].max = 1; d.dragon.moves[0].jump = false; d.dragon.moves[0].mode = 'move'; },
    }),
    toMove: 'w', solution: 'e6:wingSweep',
  },
  {
    id: 'stampede', title: 'Stampede', hint: 'Elephants do not stop for small things.',
    ruleset: rs('stampede', 'wKa1 wEe2 bPe4 bKe5 bQh8 bRa8', { rules: KING_HUNT }), toMove: 'w', solution: 'e2:stampede:e5',
  },
  {
    id: 'tongue', title: 'Come closer', hint: 'A frog tongue does not care what lies in between.',
    ruleset: rs('tongue', 'wKa1 wFd2 bKd5 bQh8 bRa8', { rules: KING_HUNT, voids: ['d3'] }), toMove: 'w', solution: 'd2:tongue:d5',
  },
  {
    id: 'smothered', title: 'Smothered', hint: 'The black king is boxed in by its own pieces.',
    ruleset: rs('smothered', 'wKg1 wNg5 bKh8 bRg8 bPg7 bPh7'), toMove: 'w', solution: 'g5-f7',
  },
  {
    id: 'corner', title: 'Into the corner', hint: 'Your king already guards the escape squares.',
    ruleset: rs('corner', 'wKb6 wQg1 bKa8'), toMove: 'w', solution: 'g1-g8',
  },
  {
    id: 'promotion', title: 'Crowning moment', hint: 'A pawn on the seventh rank wants to grow up.',
    ruleset: rs('promotion', 'wKb6 wPc7 bKa8'), toMove: 'w', solution: 'c7-c8=queen',
  },
  {
    id: 'dragon-flight', title: 'Over the wall', hint: 'Fire needs a clear line, wings do not.',
    ruleset: rs('dragon-flight', 'wKa1 wDd3 bPd4 bKd5 bQh8 bRa8', { rules: KING_HUNT }), toMove: 'w', solution: 'd3-d5',
  },
  {
    id: 'leapfrog', title: 'Leapfrog', hint: 'Frogs hop over whatever is in the way.',
    ruleset: rs('leapfrog', 'wKa1 wFd4 bPe5 bKf6 bQh8 bRa8', { rules: KING_HUNT }), toMove: 'w', solution: 'd4-f6',
  },
  {
    id: 'two-steps', title: 'Two steps', hint: 'You move twice this turn. Go around the pawn.',
    ruleset: rs('two-steps', 'wKa1 wRb1 bKh7 bPg7 bNc6', { rules: { ...KING_HUNT, movesPerTurn: 2 } }),
    toMove: 'w', solution: 'b1-h1,h1-h7',
  },
  {
    id: 'arabian', title: 'Arabian mate', hint: 'The knight already guards the square next to the king.',
    ruleset: rs('arabian', 'wKa1 wNf6 wRa7 bKh8'), toMove: 'w', solution: 'a7-h7',
  },
  {
    id: 'ladder', title: 'Ladder', hint: 'One rook holds the seventh rank, the other needs the eighth.',
    ruleset: rs('ladder', 'wKa1 wRb7 wRc1 bKh8'), toMove: 'w', solution: 'c1-c8',
  },
  {
    id: 'double-back-rank', title: 'Back rank, twice', hint: 'Two moves this turn: your own pawn is in the way, so go around it.',
    ruleset: rs('double-back-rank', 'wKa1 wRc3 wPc5 bKe8 bPd7 bPe7 bPf7', { rules: { movesPerTurn: 2 } }),
    toMove: 'w', solution: 'c3-h3,h3-h8',
  },
  {
    id: 'hedge', title: 'Hedge them in', hint: 'Here a stalemated player loses. Take away the last free square.',
    ruleset: rs('hedge', 'wKf7 wGh6 bKh8', {
      rules: { draws: { stalemate: 'loss', fiftyMove: true, threefold: true, insufficientMaterial: true } },
    }),
    toMove: 'w', solution: 'h6:plantHedge:h7',
  },
  {
    id: 'tough-bug', title: 'Tough bug', hint: 'The cockroach will survive. The king behind it will not.',
    ruleset: rs('tough-bug', 'wKa1 wEe2 bXe3 bKe4 bQh8 bRa8', { rules: KING_HUNT }), toMove: 'w', solution: 'e2:stampede:e4',
  },
  {
    id: 'cook-jump', title: 'Over the counter', hint: 'Cooks jump two squares straight, whatever is in between.',
    ruleset: rs('cook-jump', 'wKa1 wCd4 bPd5 bKd6 bQh8 bRa8', { rules: KING_HUNT }), toMove: 'w', solution: 'd4-d6',
  },
  {
    id: 'kick-boom', title: 'Kick and boom', hint: 'Your own king is too close to that bomb. Move the enemy king instead.',
    ruleset: rs('kick-boom', 'wKc3 wOd4 wOg7 bKe5 bQa8', { rules: { ...KING_HUNT, movesPerTurn: 2 } }),
    toMove: 'w', solution: 'd4:kick:e5,g7:detonate',
  },
];

/** The action for one solution step ("e2-e4", "e7-e8=queen", "d4:fireBreath:d7"), if it is legal now. */
export function puzzleStep(s: GameState, step: string): Action | null {
  const [main, promo] = step.trim().split('=');
  const [a, b, c] = main.split(/[-:]/);
  const from = parseSq(a ?? '', s.w, s.h);
  if (from < 0) return null;
  const want: Action = main.includes('-')
    ? { t: 'move', from, to: parseSq(b ?? '', s.w, s.h), promo: promo || undefined }
    : { t: 'ability', kind: b as AbilityKind, from, target: c ? parseSq(c, s.w, s.h) : undefined };
  return findLegal(s, want) ?? null;
}

/** Why a puzzle is broken (solution illegal, does not win, or a shorter win exists), or null if it is fine. */
export function checkPuzzle(p: Pick<Puzzle, 'ruleset' | 'toMove' | 'solution'>): string | null {
  const errors = validateRuleset(p.ruleset).filter((i) => i.level === 'error');
  if (errors.length) return errors[0].message;
  let s = createGame(p.ruleset);
  if (s.result) return 'The game is already over in the starting position.';
  if (s.turn !== p.toMove) return 'White always moves first in a puzzle.';
  const steps = p.solution.split(',').map((x) => x.trim()).filter(Boolean);
  if (!steps.length) return 'Enter the winning move.';
  for (const [i, step] of steps.entries()) {
    if (s.result) return `The game is already won before step ${i + 1}.`;
    if (s.turn !== p.toMove) return `Step ${i + 1} is no longer in the same turn.`;
    const a = puzzleStep(s, step);
    if (!a) return `Step ${i + 1} ("${step}") is not a legal move.`;
    s = applyAction(s, a).state;
  }
  if (s.result?.winner !== p.toMove) return 'The solution does not win.';
  return null;
}

/** The solution-step text for an action (inverse of `puzzleStep`). */
export function puzzleStepText(s: GameState, a: Action): string {
  const n = (sq: number) => sqName(sq, s.w);
  if (a.t === 'move') return `${n(a.from)}-${n(a.to)}${a.promo ? '=' + a.promo : ''}`;
  return `${n(a.from)}:${a.kind}${a.target != null && a.target >= 0 ? ':' + n(a.target) : ''}`;
}
